import i18n from '@/i18n';
const DEFAULT_MAX_ATTEMPTS = 15;
const DEFAULT_SLOW_HINT_MS = 8000;
const DEFAULT_RETRY_BASE_MS = 2000;
const DEFAULT_MAX_RETRY_MS = 8000;

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** 详情页拉取任务：网络抖动 / 超时 / 5xx 可重试 */
export function isRetryableDetailLoadError(error) {
  if (!error) return true;
  const code = String(error.code || '').trim().toUpperCase();
  if (code === 'TIMEOUT' || code === 'NETWORK_ERROR' || code === 'FETCH_FAILED') return true;
  const status = Number(error.status);
  if ([408, 429, 500, 502, 503, 504].includes(status)) return true;
  const msg = String(error.message || '');
  return /超时|网络|fetch|aborted|Failed to fetch|NetworkError|ECONNRESET/i.test(msg);
}

/**
 * 详情页任务加载：慢网络下自动重试，避免过早展示「加载失败」。
 * @param {() => Promise<any>} loadFn
 * @param {{
 *   maxAttempts?: number,
 *   slowHintMs?: number,
 *   retryBaseMs?: number,
 *   maxRetryMs?: number,
 *   onSlowLoad?: () => void,
 *   shouldRetryResult?: (result: any) => boolean,
 * }} [options]
 */
export async function loadWorkbenchDetailTaskWithRetry(loadFn, options = {}) {
  const {
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    slowHintMs = DEFAULT_SLOW_HINT_MS,
    retryBaseMs = DEFAULT_RETRY_BASE_MS,
    maxRetryMs = DEFAULT_MAX_RETRY_MS,
    onSlowLoad,
    shouldRetryResult = (result) => result == null,
  } = options;

  let slowTimer = null;
  if (typeof onSlowLoad === 'function') {
    slowTimer = window.setTimeout(onSlowLoad, slowHintMs);
  }

  try {
    let lastError = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const result = await loadFn();
        if (!shouldRetryResult(result) || attempt >= maxAttempts - 1) {
          return result;
        }
      } catch (error) {
        lastError = error;
        if (!isRetryableDetailLoadError(error) || attempt >= maxAttempts - 1) {
          throw error;
        }
      }
      await sleep(Math.min(retryBaseMs * (attempt + 1), maxRetryMs));
    }
    if (lastError) throw lastError;
    return null;
  } finally {
    if (slowTimer) window.clearTimeout(slowTimer);
  }
}

export function buildDetailLoadingHint(loadPhase) {
  if (loadPhase === 'slow') {
    return i18n.t('workbench:ipeUtilError.loadingHeavy');
  }
  return i18n.t('workbench:ipeUtilError.loadingTaskInfo');
}
